import React, { useEffect, useState, useRef } from 'react';
import Axios from 'axios';
import * as Sentry from '@sentry/react';
import { Modal, ModalBody, ModalHeader, Button } from 'reactstrap';
import {
  OneDriveResult,
  OneDrivePicker,
  PickerProps,
  LiscioFile,
  PickerOptions,
  SharePointDefaultExportFolder,
} from './types';
import {
  DMS_FILE_EXPORT,
  MICROSOFT_CLIENT_ID,
  OAUTH_ACCESS_TOKENS,
  SHAREPOINT_EXPORT_SETTINGS,
} from '../../constants/constants';
import { AlertMessage } from '../../utilities/utils';

declare const OneDrive: OneDrivePicker;

interface FolderDetails extends SharePointDefaultExportFolder {
  name?: string;
  path?: string;
}

const createGraphHeaders = (token: string) => ({
  headers: {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  },
});

export default function Picker({
  startUpload = () => {},
  cancelWidget,
  closeModal,
  selectedRecords,
  mode,
  appKey,
}: PickerProps) {
  const [accessToken, setAccessToken] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [siteUrl, setSiteUrl] = useState('');
  const [defaultExportFolder, setDefaultExportFolder] = useState<FolderDetails | null>(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [shouldLaunchPicker, setShouldLaunchPicker] = useState(false);
  const pickerLaunched = useRef(false);
  const isMounted = useRef(true);
  const exportMode = mode === 'export';
  const graphApiBaseUrl = 'https://graph.microsoft.com/v1.0';
  const graphEndpoint = appKey === 'onedrive' ? 'drives' : 'sites?search=*';
  const headers = createGraphHeaders(accessToken);

  const fetchAccessToken = async () => {
    try {
      // Fetch access token
      const tokenResponse = await Axios.get(`${OAUTH_ACCESS_TOKENS}/${appKey}`);
      if (!isMounted.current) return;
      
      if (tokenResponse.status !== 200) return;
      
      const token = tokenResponse.data.access_token;
      setAccessToken(token);
      
      // Fetch export settings separately (only for SharePoint export mode)
      let folder: FolderDetails | null = null;
      if (appKey === 'sharepoint' && exportMode) {
        try {
          const settingsResponse = await Axios.get(SHAREPOINT_EXPORT_SETTINGS);
          if (isMounted.current && settingsResponse.data.default_export_folder) {
            folder = settingsResponse.data.default_export_folder;
            setDefaultExportFolder(folder);
          }
        } catch (error: any) {
          // 404 is expected if no settings configured - continue without default folder
          // Other errors (500, network issues, etc.) should be shown to user
          const status = error?.response?.status;
          if (status && status !== 404) {
            Sentry.captureException(error, {
              tags: { component: 'SharePointPicker', operation: 'fetchExportSettings' },
              extra: { status },
            });
            AlertMessage('error', 'Unable to load your saved export folder. You can still select a folder manually.', 4000);
          }
        }
      }
      
      // Pass the token and folder directly to avoid React 17 batching issues
      fetchSiteUrl(token, folder);
    } catch (error: any) {
      if (!isMounted.current) return;
      const status = error?.response?.status;
      Sentry.captureException(error, {
        tags: { component: 'SharePointPicker', operation: 'fetchAccessToken' },
        extra: { status, appKey },
      });
      if (status === 401 || status === 403) {
        AlertMessage('error', 'Your SharePoint connection has expired. Please reconnect in Settings > Third Party Apps.', 5000);
      } else {
        AlertMessage('error', 'Unable to connect to SharePoint. Please check your connection and try again.', 4000);
      }
      cancelWidget('');
      if (exportMode) closeModal();
    }
  };

  useEffect(() => {
    isMounted.current = true;
    fetchAccessToken();
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Launch picker when ready and not showing export options
  useEffect(() => {
    if (shouldLaunchPicker && accessToken && siteUrl && !pickerLaunched.current) {
      pickerLaunched.current = true;
      launchPicker();
    }
  }, [shouldLaunchPicker, accessToken, siteUrl]);

  const buildBreadcrumbPath = (
    driveName: string,
    parentPath: string | undefined,
    folderName: string
  ): string => {
    // parentPath can be:
    // - "/drive/root:" or "/drive/root:/Subfolder/Another"
    // - "/drives/{driveId}/root:" or "/drives/{driveId}/root:/Subfolder/Another"
    // We want to build a breadcrumb like "Library > Subfolder > FolderName"
    const parts: string[] = [];
    
    // Add drive/library name (e.g., "Documents")
    if (driveName) {
      parts.push(driveName);
    }
    
    // Extract and add parent folders from path
    if (parentPath) {
      // Match both /drive/root: and /drives/{id}/root: formats
      const pathMatch = parentPath.match(/\/root:?\/?(.*)$/);
      const parentFolder = pathMatch?.[1] || '';
      if (parentFolder) {
        // Split by / and add each part (these are intermediate folders)
        const folderParts = parentFolder.split('/').filter(Boolean);
        parts.push(...folderParts);
      }
    }
    
    // Add the selected folder name
    parts.push(folderName);
    
    // If path is too long (more than 4 parts), truncate with "..."
    // Show: first part > ... > second-to-last > last
    const maxParts = 4;
    if (parts.length > maxParts) {
      const first = parts[0];
      const lastTwo = parts.slice(-2);
      return [first, '...', ...lastTwo].join(' > ');
    }
    
    return parts.join(' > ');
  };

  const fetchFolderDetails = async (token: string, folder: FolderDetails) => {
    const authHeaders = createGraphHeaders(token);
    
    try {
      // Fetch drive info and folder info in parallel
      const [driveResponse, folderResponse] = await Promise.all([
        Axios.get(`${graphApiBaseUrl}/drives/${folder.drive_id}`, authHeaders),
        Axios.get(`${graphApiBaseUrl}/drives/${folder.drive_id}/items/${folder.folder_id}`, authHeaders),
      ]);
      
      if (!isMounted.current) return;
      
      const driveName = driveResponse.data.name || '';
      const { name, parentReference } = folderResponse.data;
      
      if (name) {
        const fullPath = buildBreadcrumbPath(driveName, parentReference?.path, name);
        setDefaultExportFolder((prev) =>
          prev ? { ...prev, name, path: fullPath } : null
        );
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        // 404 means folder was deleted - clear and let user pick new folder
        setDefaultExportFolder(null);
        AlertMessage('warning', 'Your saved export folder no longer exists in SharePoint. Please select a new folder.', 4000);
      } else if (status === 403) {
        // 403 means user doesn't have access (e.g., firm folder set by another user)
        setDefaultExportFolder(null);
        AlertMessage('info', 'You don\'t have access to the saved folder. Please select your own folder.', 4000);
      } else if (status && status >= 500) {
        Sentry.captureException(error, {
          tags: { component: 'SharePointPicker', operation: 'fetchFolderDetails' },
          extra: { status, folderId: folder.folder_id, driveId: folder.drive_id },
        });
        AlertMessage('error', 'Unable to load folder details from SharePoint. You can still select a folder manually.', 4000);
      }
    }
  };

  const fetchSiteUrl = (token: string, folder: FolderDetails | null) => {
    const authHeaders = createGraphHeaders(token);
    
    Axios.get(`${graphApiBaseUrl}/${graphEndpoint}`, authHeaders)
      .then((response) => {
        if (!isMounted.current) return;
        const site = response.data.value[0];
        const url = appKey === 'onedrive'
          ? site.webUrl
          : `${site.webUrl}/Shared%20Documents`;
        setSiteUrl(url);
        setIsLoading(false);

        // If SharePoint export mode with a saved folder, show the options UI
        // Otherwise, trigger the picker launch
        if (exportMode && appKey === 'sharepoint' && folder) {
          setShowExportOptions(true);
          // Fetch folder details for display name
          fetchFolderDetails(token, folder);
        } else {
          setShouldLaunchPicker(true);
        }
      })
      .catch((error: any) => {
        if (!isMounted.current) return;
        Sentry.captureException(error, {
          tags: { component: 'SharePointPicker', operation: 'fetchSiteUrl' },
          extra: { appKey },
        });
        AlertMessage('error', 'Unable to connect to SharePoint. Please try again.', 4000);
      });
  };

  const exportToDefaultFolder = () => {
    if (!defaultExportFolder) return;

    setIsExporting(true);
    const data = {
      app_key: appKey,
      remote_directory: `drives/${defaultExportFolder.drive_id}/items/${defaultExportFolder.folder_id}`,
      document_ids: selectedRecords.map((a) => a.id),
      folder_metadata: {
        folder_id: defaultExportFolder.folder_id,
        drive_id: defaultExportFolder.drive_id,
        endpoint: defaultExportFolder.endpoint,
        path: defaultExportFolder.path,
      },
    };

    Axios.post(DMS_FILE_EXPORT, data)
      .then((r) => {
        AlertMessage('success', r.data.message, 3000);
        cancelWidget('');
        closeModal();
      })
      .catch((error: any) => {
        Sentry.captureException(error, {
          tags: { component: 'SharePointPicker', operation: 'exportToDefaultFolder' },
          extra: { appKey, folderId: defaultExportFolder.folder_id },
        });
        AlertMessage('error', 'Export failed. Please try again or select a different folder.', 4000);
        setIsExporting(false);
      });
  };

  const launchPicker = () => {
    const odOptions: PickerOptions = {
      clientId: MICROSOFT_CLIENT_ID,
      action: 'query',
      viewType: exportMode ? 'folders' : 'files',
      multiSelect: false,
      advanced: {
        accessToken,
        endpointHint: siteUrl,
        scopes: ['Files.ReadWrite.All'],
      },
      success: async (response: OneDriveResult) => {
        const object = response.value[0];
        if (exportMode) {
          // Fetch drive and folder details to get the full breadcrumb path
          let folderPath: string | undefined;
          try {
            const [driveResponse, folderResponse] = await Promise.all([
              Axios.get(`${graphApiBaseUrl}/drives/${object.parentReference.driveId}`, headers),
              Axios.get(`${graphApiBaseUrl}/drives/${object.parentReference.driveId}/items/${object.id}`, headers),
            ]);
            const driveName = driveResponse.data.name || '';
            const { name, parentReference } = folderResponse.data;
            folderPath = buildBreadcrumbPath(driveName, parentReference?.path, name);
          } catch (error: any) {
            // Path fetch failed - export will still work, just won't save the display path
            const status = error?.response?.status;
            if (status && status >= 500) {
              Sentry.captureException(error, {
                tags: { component: 'SharePointPicker', operation: 'fetchFolderPathOnExport' },
                extra: { status, driveId: object.parentReference.driveId, folderId: object.id },
              });
            }
          }

          const data = {
            app_key: appKey,
            remote_directory: `drives/${object.parentReference.driveId}/items/${object.id}`,
            document_ids: selectedRecords.map((a) => a.id),
            folder_metadata: {
              folder_id: object.id,
              drive_id: object.parentReference.driveId,
              endpoint: object.endpoint,
              path: folderPath,
            },
          };
          Axios.post(DMS_FILE_EXPORT, data)
            .then((r) => {
              AlertMessage('success', r.data.message, 3000);
            })
            .catch((error: any) => {
              Sentry.captureException(error, {
                tags: { component: 'SharePointPicker', operation: 'exportViaPicker' },
                extra: { appKey, folderId: object.id, driveId: object.parentReference.driveId },
              });
              AlertMessage('error', 'Export failed. Please try again or select a different folder.', 4000);
            });
          cancelWidget('');
          closeModal();
        } else {
          const downloadUrl = `${graphApiBaseUrl}/drives/${object.parentReference.driveId}/items/${object.id}`;
          Axios.get(downloadUrl, headers)
            .then((r) => {
              if (!isMounted.current) return;
              const fileData: LiscioFile = {
                name: r.data.name,
                type: r.data.file.mimeType,
                '@microsoft.graph.downloadUrl':
                  r.data['@microsoft.graph.downloadUrl'],
              };
              AlertMessage('success', 'File uploading', 3000);
              startUpload([fileData], appKey);
            })
            .catch((error: any) => {
              if (!isMounted.current) return;
              Sentry.captureException(error, {
                tags: { component: 'SharePointPicker', operation: 'downloadFile' },
                extra: { appKey, driveId: object.parentReference.driveId, fileId: object.id },
              });
              AlertMessage('error', 'Unable to download file from SharePoint. Please try again.', 4000);
            });
          cancelWidget('');
        }
      },
      cancel: () => {
        cancelWidget('');
        if (exportMode) closeModal();
      },
      error: (error: any) => {
        Sentry.captureException(error, {
          tags: { component: 'SharePointPicker', operation: 'pickerError' },
          extra: { appKey, exportMode },
        });
        AlertMessage('error', 'SharePoint picker encountered an error. Please try again.', 4000);
        cancelWidget('');
        if (exportMode) closeModal();
      },
    };

    OneDrive.open(odOptions);
  };

  const handlePickFolder = () => {
    setShowExportOptions(false);
    pickerLaunched.current = false;
    setShouldLaunchPicker(true);
  };

  const handleCancel = () => {
    cancelWidget('');
    closeModal();
  };

  const loading = () => (
    <div className="col-sm-12">
      <div
        id="loading"
        className="d-block"
        role="status"
        aria-label="Loading SharePoint picker"
      />
    </div>
  );

  // Show export options UI when we have a default folder saved
  if (showExportOptions && defaultExportFolder && exportMode) {
    return (
      <Modal
        isOpen
        className="customModal customModal--center customModal--alert customModal--w500 customModal--nopadding"
      >
        <ModalHeader className="ModalHeader">
          <button
            type="button"
            onClick={handleCancel}
            className="close"
            aria-label="Close"
          >
            <i className="icon-close2" />
          </button>
        </ModalHeader>
        <ModalHeader className="modalHeader">
          <div className="text-medium pl-2">Export to SharePoint</div>
        </ModalHeader>
        <ModalBody className="modalContent">
          <div className="modalContent--inner pb-3">
            <p className="mb-3">
              Export {selectedRecords.length} file{selectedRecords.length !== 1 ? 's' : ''} to SharePoint
            </p>
            <div className="d-flex flex-column gap-2">
              <Button
                color="primary"
                className="btn-block mb-2"
                onClick={exportToDefaultFolder}
                disabled={isExporting}
              >
                {isExporting
                  ? 'Exporting...'
                  : `Export to "${defaultExportFolder.path || defaultExportFolder.name || 'Saved Folder'}"`}
              </Button>
              <Button
                color="secondary"
                outline
                className="btn-block"
                onClick={handlePickFolder}
                disabled={isExporting}
              >
                Pick a different folder
              </Button>
            </div>
          </div>
          <div className="row">
            <div className="col-12 justify-content-end formbtn mb-4">
              <Button
                className="btn btn-outline-light"
                onClick={handleCancel}
                disabled={isExporting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    );
  }

  // Default behavior: show loading while picker is being launched
  return (
    <div>
      {isLoading && loading()}
    </div>
  );
}
